import { Component, Inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CourseRepositoryService } from '../../../courses/services/course-repository.service';
import { AppMetaData, AppMetaDataItemNames, AssignmentCourseLessonEntity, AssignmentEntity, CourseEntity, StudentEntity } from '../../../../meta-data/app-meta-data.service';
import { TreeNode } from 'primeng/api';
import { DwComponent, DwComponentType, DwExpressionService, DwMenuService, DwMetaDataFormApi, DwMetaDataFormStateService, DwMetaDataService, DwMetaDataServiceToken, DwSectionBaseComponent, DwUiConfigRegistryService, DwUIMetaDataConfig, DwUIMetaDataConfigToken } from '@devwareapps/devware-cap';
import { AbstractControl, FormControl } from '@angular/forms';
import { AssignmentVariableContext } from '../../models/assignment-variable-context.model';
import { debounceTime } from 'rxjs/operators';
import { StudentRepositoryService } from '../../../students/services/student-repository.service';

@DwComponent({
  key: 'lesson-selection-tree-control',
  name: 'Lesson Selection Tree Control',
  componentType: DwComponentType.formSection,
  isGlobal: false,
  parentItemName: AppMetaDataItemNames.Assignment
})
@Component({
  selector: 'app-lesson-selection-tree-form-control',
  template: `
    <!-- Parent selected Ids: {{selectedLessonIds | json}}<br>
    // List Value: {{associativeList?.value | json}}<br> -->

    <div class="lesson-tree-container">
      <app-lesson-selection-tree-control [courseId]="courseIdFC?.value" [(selectedLessonIds)]="selectedLessonIds" (selectedLessonIdsChange)="updateLessons()"
       [studentId]="studentId" [showStudentProgress]="showStudentProgress">
      </app-lesson-selection-tree-control>
      <div class="invalid-feedback lesson-selection-validation" *ngIf="showLessonValidationMessage">
        At least one lesson must be selected.
      </div>
    </div>
  `,
  styles: [`
    .lesson-selection-validation {
      display: block;
      margin-top: var(--space-2, 8px);
    }
  `]
})
export class LessonSelectionTreeFormControlComponent extends DwSectionBaseComponent {
  associativeList: AbstractControl;

  controlName: string = 'AssignmentCourseLesson';

  courseId: number = 1;

  selectedLessonIds: number[] = [];
  courseIdFC: FormControl;
  formApi: DwMetaDataFormApi;
  variableContext: AssignmentVariableContext;

  studentId: number;
  showStudentProgress: boolean = false;
  showLessonValidationMessage = false;

  constructor(
    dwExpressionService: DwExpressionService,
    dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    private dwFormStateService: DwMetaDataFormStateService,
    private courseRepositoryService: CourseRepositoryService,
    private dwMenuService: DwMenuService,
    private studentRepositoryService: StudentRepositoryService
  ) {
    super(dwExpressionService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    this.formApi = this.dwFormStateService.state.formApi;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.associativeList = this.formGroup.get(this.controlName);
    this.courseIdFC = this.formGroup.get('CourseId') as FormControl;

    if (!this.associativeList) {
      console.warn(`Form control ${this.controlName} not found in form group. This is required for the lesson selection tree control to work.`);
      return;
    }

    this.configureLessonValidator();

    this.runAfterFormDataLoaded(() => {
      this.initSelectedStudent();
    });

    this.formApi.getFormDataChanges()
      .pipe(debounceTime(200))
      .subscribe(() => {
        this.setupSelectedLessons();

        this.updateSelectedStudent();
      });

    //this.updateSelectedStudent();
  }

  setupSelectedLessons() {
    const allLessons: AssignmentCourseLessonEntity[] = this.associativeList.value || [];

    this.selectedLessonIds = allLessons.filter(l => !l._isDeleted).map(l => l.CourseLessonId);
  }

  updateSelectedStudent() {
    this.variableContext = this.resolveVariableContext();
    const assignment = this.formApi.getFormData<AssignmentEntity>();

    this.showStudentProgress = false;

    const currentStudents = assignment?.AssignmentStudent?.filter(as => !as._isDeleted) || [];

    if (currentStudents.length <= 1 || (this.variableContext?.ShowStudentProgressWhenMultiple && currentStudents.length > 0)) {
      const studentId = currentStudents[0]?.StudentId;

      if (this.variableContext && this.variableContext.StudentId != studentId) {
        this.variableContext.StudentId = studentId;
        this.studentId = studentId;

        this.formApi.patchVariableContext({ StudentId: studentId });
      } else {
        this.studentId = studentId;
      }

      if (!assignment?.UseGroupAssignment && studentId) {
        this.showStudentProgress = true;
      }
    }

    this.subscriptions.push(
      this.associativeList.statusChanges.subscribe(() => this.updateLessonValidationMessage())
    );
  }

  initComplete = false;

  initSelectedStudent() {
    if (this.initComplete) {
      return;
    }

    this.initComplete = true;

    const paramMap = (this.dwMenuService.menuState.currentParams as any)?.map;

    if (!paramMap) {
      return;
    }

    const userId = paramMap['dw_UserId'];

    if (userId) {
      return this.studentRepositoryService.getStudentFromUserId(userId).subscribe(student => {

        this.setInitialStudent(student);

      });
    }
  }

  setInitialStudent(student: StudentEntity) {
    this.variableContext = this.resolveVariableContext();
    const assignment = this.formApi.getFormData<AssignmentEntity>();

    if (student?.StudentId && (assignment?.AssignmentStudent?.length || 0) == 0) {
      this.studentId = student.StudentId;

      const assignmentStudent = AppMetaData.AssignmentStudent.CreateEntity();
      assignmentStudent.StudentId = student.StudentId;

      this.formApi.patchFormData({ AssignmentStudent: [assignmentStudent] });

      if (this.variableContext) {
        this.variableContext.StudentId = student.StudentId;
        this.formApi.patchVariableContext({ StudentId: student.StudentId });
      }
    }    
  }

  updateLessons() {
    const allLessons: AssignmentCourseLessonEntity[] = this.associativeList.value || [];

    for (let lesson of allLessons) {
      lesson._isDeleted = true;
    }

    for (let lessonId of this.selectedLessonIds) {
      let lesson = allLessons.find(l => l.CourseLessonId == lessonId);

      if (lesson) {
        lesson._isDeleted = false;
      } else {

        lesson = AppMetaData.AssignmentCourseLesson.CreateEntity();

        lesson.CourseLessonId = lessonId;

        allLessons.push(lesson);
      }
    }

    this.associativeList.setValue(allLessons);
    this.associativeList.markAsTouched();
    this.updateLessonValidationMessage();
  }

  protected submittedChanged(): void {
    this.updateLessonValidationMessage();
  }

  private updateLessonValidationMessage(): void {
    this.showLessonValidationMessage = !!this.submitted
      && !!this.associativeList?.errors?.lessonCountError;
  }

  private configureLessonValidator(): void {
    const existingValidator = this.associativeList.validator;
    const lessonValidator = (control: AbstractControl) => {
      const lessons = (control.value || []) as AssignmentCourseLessonEntity[];
      const hasSelectedLesson = lessons.some(lesson => lesson._isDeleted !== true && Number(lesson.CourseLessonId) > 0);

      return hasSelectedLesson ? null : { lessonCountError: { value: true } };
    };

    this.associativeList.setValidators(existingValidator ? [existingValidator, lessonValidator] : [lessonValidator]);
    this.associativeList.updateValueAndValidity({ emitEvent: false });
  }

  ngOnChanges(changes: SimpleChanges): void {

  }

  private resolveVariableContext(): AssignmentVariableContext {
    try {
      return this.formApi?.getVariableContextValue<AssignmentVariableContext>();
    } catch {
      return null;
    }
  }
}
